import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getContracts, getContract } from '../../services/api';

const PERIOD_ORDER = ['Base', 'OY1', 'OY2', 'OY3', 'OY4', 'OY5', 'OY6', 'OY7', 'OY8', 'OY9', 'Overall'];

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusBadgeClass(status) {
  if (!status) return '';
  switch (status) {
    case 'Active': return 'active';
    case 'Exercised': return 'exercised';
    case 'Not Exercised': return 'not-exercised';
    case 'Overall': return 'overall';
    case 'Proposed': return 'pending';
    default: return status.toLowerCase().replace(/\s+/g, '-');
  }
}

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    const idxA = PERIOD_ORDER.indexOf(a.year);
    const idxB = PERIOD_ORDER.indexOf(b.year);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });
}

function compareValues(a, b, key, type) {
  let valA = a[key];
  let valB = b[key];

  if (type === 'number') {
    valA = Number(valA) || 0;
    valB = Number(valB) || 0;
    return valA - valB;
  }
  if (type === 'date') {
    const dA = valA ? new Date(valA).getTime() : 0;
    const dB = valB ? new Date(valB).getTime() : 0;
    return dA - dB;
  }
  return (valA || '').toLowerCase().localeCompare((valB || '').toLowerCase());
}

const COLUMNS = [
  { key: 'expand', label: '', sortable: false },
  { key: 'projectName', label: 'Project', sortable: true, type: 'string' },
  { key: 'contractNumber', label: 'Contract #', sortable: true, type: 'string' },
  { key: 'customer', label: 'Prime', sortable: true, type: 'string' },
  { key: 'division', label: 'Division', sortable: true, type: 'string' },
  { key: 'contractType', label: 'Type', sortable: true, type: 'string' },
  { key: 'contractValue', label: 'Contract Value', sortable: true, type: 'number' },
  { key: 'totalFundingITD', label: 'Funded ITD', sortable: true, type: 'number' },
  { key: 'endDate', label: 'POP End', sortable: true, type: 'date' },
  { key: 'status', label: 'Status', sortable: true, type: 'string' },
];

function CustomerDetail({ customerName, onNavigate }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('projectName');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedContract, setExpandedContract] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await getContracts();
        setContracts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const customerContracts = useMemo(() => {
    return contracts.filter((c) => (c.endCustomer || c.customer) === customerName);
  }, [contracts, customerName]);

  const summary = useMemo(() => {
    let totalValue = 0;
    const contractTypes = new Set();
    const divisions = new Set();
    let activeCount = 0;

    customerContracts.forEach((c) => {
      totalValue += Number(c.contractValue) || 0;
      if (c.contractType) contractTypes.add(c.contractType);
      if (c.division) divisions.add(c.division);
      if (c.status === 'Active') activeCount += 1;
    });

    return {
      count: customerContracts.length,
      totalValue,
      contractTypes: Array.from(contractTypes).sort(),
      divisions: Array.from(divisions).sort(),
      activeCount,
    };
  }, [customerContracts]);

  const sorted = useMemo(() => {
    const colDef = COLUMNS.find((c) => c.key === sortKey);
    const type = colDef ? colDef.type : 'string';
    const list = [...customerContracts];
    list.sort((a, b) => {
      const cmp = compareValues(a, b, sortKey, type);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [customerContracts, sortKey, sortDir]);

  const handleSort = useCallback((key) => {
    if (key !== sortKey) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey('projectName');
      setSortDir('asc');
    }
  }, [sortKey, sortDir]);

  const handleRowClick = useCallback(async (contractNumber) => {
    if (expandedContract === contractNumber) {
      setExpandedContract(null);
      setPeriods([]);
      return;
    }
    setExpandedContract(contractNumber);
    setPeriodsLoading(true);
    setPeriods([]);
    try {
      const data = await getContract(contractNumber);
      const nonOverall = data.filter((p) => p.year !== 'Overall');
      setPeriods(sortPeriods(nonOverall));
    } catch (err) {
      setPeriods([]);
    } finally {
      setPeriodsLoading(false);
    }
  }, [expandedContract]);

  if (!customerName) {
    return (
      <div>
        <button className="form-back-link" onClick={() => onNavigate('companies')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Customers
        </button>
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--bcore-text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontSize: '13px',
        }}>
          No customer selected. Go back to select a customer.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Link */}
      <button className="form-back-link" onClick={() => onNavigate('companies')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Customers
      </button>

      {/* Customer Name */}
      <h2 style={{
        fontFamily: '"Kode Mono", monospace',
        textTransform: 'uppercase',
        fontSize: '24px',
        fontWeight: 700,
        letterSpacing: '1px',
        marginBottom: '8px',
        marginTop: '16px',
      }}>
        {customerName}
      </h2>

      {/* Contract Type Tags */}
      {!loading && summary.contractTypes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '24px' }}>
          {summary.contractTypes.map((type) => (
            <span className="tag" key={type}>{type}</span>
          ))}
        </div>
      )}

      {/* Stat Cards */}
      {!loading && !error && (
        <div className="stat-cards" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Contracts</span>
              <div className="stat-card-icon accent">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              </div>
            </div>
            <div className="stat-card-value">{summary.count}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Portfolio Value</span>
              <div className="stat-card-icon green">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
              </div>
            </div>
            <div className="stat-card-value">{formatCurrency(summary.totalValue)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Divisions</span>
              <div className="stat-card-icon blue">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>
              </div>
            </div>
            <div className="stat-card-value">{summary.divisions.length}</div>
            {summary.divisions.length > 0 && (
              <div className="stat-card-sub">{summary.divisions.join(', ')}</div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Active</span>
              <div className="stat-card-icon green">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
            </div>
            <div className="stat-card-value">{summary.activeCount}</div>
          </div>
        </div>
      )}

      {/* Section Label */}
      <div style={{
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        color: 'var(--bcore-text-dim)',
        borderBottom: '1px solid var(--bcore-border)',
        paddingBottom: '8px',
        marginBottom: '16px',
      }}>
        Contracts
      </div>

      {/* Data Table */}
      <div className="data-table-wrapper">
        {loading && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--bcore-text-dim)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontSize: '13px',
          }}>
            Loading customer data...
          </div>
        )}
        {error && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--status-critical)',
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}
        {!loading && !error && (
          <table className="data-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={col.sortable ? 'sortable-th' : ''}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  >
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      <span className="sort-indicator">
                        {sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const isExpanded = expandedContract === c.contractNumber;
                return (
                  <React.Fragment key={c.PK + c.SK}>
                    <tr
                      className={`contract-row${isExpanded ? ' expanded' : ''}`}
                      onClick={() => handleRowClick(c.contractNumber)}
                    >
                      <td className="expand-cell">
                        <svg
                          className={`expand-icon${isExpanded ? ' open' : ''}`}
                          width="12" height="12" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </td>
                      <td className="cell-primary">{c.projectName || '--'}</td>
                      <td>{c.contractNumber || '--'}</td>
                      <td>{c.customer || '--'}</td>
                      <td>{c.division || '--'}</td>
                      <td>{c.contractType || '--'}</td>
                      <td>{formatCurrency(c.contractValue)}</td>
                      <td>{formatCurrency(c.totalFundingITD)}</td>
                      <td>{formatDate(c.endDate)}</td>
                      <td>
                        <span className={`badge-status ${statusBadgeClass(c.status)}`}>
                          <span className="badge-dot"></span>
                          {c.status || '--'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="periods-container-row">
                        <td colSpan={COLUMNS.length}>
                          {periodsLoading ? (
                            <div className="periods-loading">Loading periods...</div>
                          ) : periods.length === 0 ? (
                            <div className="periods-loading">No period data found</div>
                          ) : (
                            <table className="periods-table">
                              <thead>
                                <tr>
                                  <th>Period</th>
                                  <th>Status</th>
                                  <th>Start Date</th>
                                  <th>End Date</th>
                                  <th>Contract Value</th>
                                  <th>Funding/Year</th>
                                  <th>Funded ITD</th>
                                </tr>
                              </thead>
                              <tbody>
                                {periods.map((p) => {
                                  const periodClass =
                                    p.status === 'Active' ? 'period-active' :
                                    p.status === 'Exercised' ? 'period-exercised' :
                                    p.status === 'Not Exercised' ? 'period-not-exercised' : '';
                                  return (
                                    <tr key={p.SK} className={`period-row ${periodClass}`}>
                                      <td className="period-year">{p.year || '--'}</td>
                                      <td>
                                        <span className={`badge-status ${statusBadgeClass(p.status)}`}>
                                          <span className="badge-dot"></span>
                                          {p.status || '--'}
                                        </span>
                                      </td>
                                      <td>{formatDate(p.startDate)}</td>
                                      <td>{formatDate(p.endDate)}</td>
                                      <td>{formatCurrency(p.contractValue)}</td>
                                      <td>{formatCurrency(p.fundingPerYear)}</td>
                                      <td>{formatCurrency(p.totalFundingITD)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ textAlign: 'center', padding: '40px', color: 'var(--bcore-text-dim)' }}>
                    No contracts found for this customer
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CustomerDetail;
